// Roster-swap reference remap (SOR-210 follow-up — cert Sub-12 ghost-ID timing).
//
// Selecting an existing JTI case (OrderInfoTab.handleCaseSelected) replaces the
// party AND attorney rosters wholesale with fresh crypto.randomUUID ids. Per-doc
// metadataValues still reference the PRE-swap local ids, so an immediate
// fee-calc fails (e.g. EFM 4082 "Missing Required Metadata: Party Served")
// before the DocumentsTab reconciliation effect runs.
//
// The DocumentsTab effect only ever sees the POST-swap roster, so the best it
// can do is DROP orphaned references (emptying PARTY_SERVED / FILING_ATTORNEY,
// forcing the user to re-check them). At case-attach time, however, we still
// hold BOTH the old and new rosters — so we can REMAP old→new local ids and
// carry the references over intact. This runs synchronously with the swap, so
// it no longer depends on which wizard tab happens to be mounted.
//
// Matching is by stable identity first (court externalId — survives re-selecting
// the same case), then by normalized name (covers OCR-prefilled parties that
// have no externalId yet). References whose old id resolves to neither are
// dropped (genuine orphans); ids that were never in the old roster (already-new
// ids, codeList values, etc.) pass through untouched. new-data inline payloads
// (NEW_ATTORNEY, NEW_*_PARTY — object arrays, not string arrays) are left alone.
//
// The retargeted reference array is order-preserving-deduped (SOR-219): the
// case-attach roster is now deduplicated to one canonical party per court id, so
// two pre-swap duplicate ids both remap to the same canonical id — a legacy draft
// that stored BOTH collapses to a single canonical entry instead of doubling it,
// preserving the SPEC-EFILING-INVARIANTS R8/R9 resolved-or-dropped-never-both
// closure.

export interface RemapPartyLike {
  id: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
}

export interface RemapAttorneyLike {
  id: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  barNumber?: string;
}

export interface RemapDocLike {
  id: string;
  metadataValues?: Record<string, unknown>;
}

export interface DocMetadataPatch {
  docId: string;
  /** Only the keys whose reference arrays changed. */
  metadataValues: Record<string, string[]>;
  /**
   * The codes whose array lost at least one pre-swap id with no identity match
   * (SPEC-SOR-220-v2 R2): the drop this module used to make silently is
   * REPORTED, so the attach site can stamp those slots' provenance `cleared`
   * and the draft can tell "just cleared" from "never chosen". A patch that
   * only rewrites ids reports none. Always present — empty when nothing was
   * dropped — and every reported code also appears in `metadataValues`, since a
   * pure drop always shrinks the array.
   */
  droppedCodes: string[];
}

const norm = (s: string | undefined): string =>
  (s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const partyNameKey = (p: RemapPartyLike): string =>
  norm(p.organizationName) || norm(`${p.firstName ?? ''} ${p.lastName ?? ''}`);

const attorneyNameKey = (a: RemapAttorneyLike): string =>
  norm(a.barNumber) || norm(`${a.firstName ?? ''} ${a.lastName ?? ''}`);

function buildRemap<T extends { id: string; externalId?: string }>(
  oldRoster: T[],
  newRoster: T[],
  nameKey: (m: T) => string,
  remap: Map<string, string>,
): void {
  const newByExternalId = new Map<string, string>();
  const newByName = new Map<string, string>();
  for (const m of newRoster) {
    if (m.externalId) newByExternalId.set(m.externalId, m.id);
    const k = nameKey(m);
    // First writer wins so a duplicate name doesn't clobber an earlier match.
    if (k && !newByName.has(k)) newByName.set(k, m.id);
  }
  for (const o of oldRoster) {
    if (o.externalId && newByExternalId.has(o.externalId)) {
      remap.set(o.id, newByExternalId.get(o.externalId)!);
      continue;
    }
    const k = nameKey(o);
    if (k && newByName.has(k)) remap.set(o.id, newByName.get(k)!);
  }
}

export interface RemapInput {
  documents: RemapDocLike[];
  oldParties: RemapPartyLike[];
  newParties: RemapPartyLike[];
  oldAttorneys: RemapAttorneyLike[];
  newAttorneys: RemapAttorneyLike[];
}

/**
 * Compute per-document metadata patches that remap pre-swap roster ids to
 * post-swap ids. Returns one patch per CHANGED document (empty array → nothing
 * to do). Pure: callers apply the patches via updateDocumentMetadata.
 */
export function remapMetadataReferences(input: RemapInput): DocMetadataPatch[] {
  const remap = new Map<string, string>();
  buildRemap(input.oldParties, input.newParties, partyNameKey, remap);
  buildRemap(input.oldAttorneys, input.newAttorneys, attorneyNameKey, remap);

  const oldIds = new Set<string>([
    ...input.oldParties.map(p => p.id),
    ...input.oldAttorneys.map(a => a.id),
  ]);

  // Nothing to translate (e.g. first attach with no prior roster) → no patches.
  if (remap.size === 0 && oldIds.size === 0) return [];

  const patches: DocMetadataPatch[] = [];
  for (const doc of input.documents) {
    const mv = doc.metadataValues ?? {};
    const patch: Record<string, string[]> = {};
    const droppedCodes: string[] = [];
    for (const [code, value] of Object.entries(mv)) {
      // Only existing-data reference fields persist as string arrays of local
      // ids. new-data inline payloads (NEW_ATTORNEY, …) are object arrays and
      // are skipped by the all-strings guard.
      if (!Array.isArray(value) || !value.every((v): v is string => typeof v === 'string')) {
        continue;
      }
      const next: string[] = [];
      const seen = new Set<string>();
      let dropped = false;
      for (const id of value) {
        let target: string;
        if (remap.has(id)) target = remap.get(id)!;
        else if (oldIds.has(id)) {
          // orphaned pre-swap id with no match → drop, and report the drop so
          // the attach site can stamp the slot `cleared` (SPEC-SOR-220-v2 R2).
          dropped = true;
          continue;
        } else target = id; // not a roster id → leave untouched
        // Order-preserving dedup: when the roster was deduped, two pre-swap
        // duplicate ids both remap to the one canonical id, so a legacy draft that
        // stored BOTH collapses to a single canonical entry rather than doubling
        // it (SOR-219, SPEC-EFILING-INVARIANTS R8/R9). A genuine duplicate id is
        // likewise emitted once.
        if (seen.has(target)) continue;
        seen.add(target);
        next.push(target);
      }
      if (next.length !== value.length || next.some((id, i) => id !== value[i])) {
        patch[code] = next;
      }
      if (dropped) droppedCodes.push(code);
    }
    if (Object.keys(patch).length > 0) {
      patches.push({ docId: doc.id, metadataValues: patch, droppedCodes });
    }
  }
  return patches;
}
